import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {booleanAttribute, ChangeDetectionStrategy, Component, computed, DestroyRef, Inject, Injectable, InjectionToken, input, Signal, signal, viewChild} from "@angular/core";
import {EnkelGruppeModell, EnkelPlanModell, GruppeId, GruppeModell, MøteModellEnkel, PlanId, PlanRessursClient} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {MatSelect, MatSelectModule} from "@angular/material/select";
import {FormControl, ReactiveFormsModule} from "@angular/forms";
import {MatFormFieldControl} from "@angular/material/form-field";
import {harSammeId} from "felles-ng2/modell/modellHjelpere";
import {combineLatestWith, filter, firstValueFrom, Subject, switchMap} from "rxjs";
import {TranslateModule} from "@ngx-translate/core";
import {MatFormFieldControlProxy} from "web-ng2/delt/material/MatFormFieldControlProxy";
import {FormaterZonedDateTimePipe} from "felles-ng2/pipes/FormaterZonedDateTimePipe";
import {GruppeDatatjeneste} from "felles-ng2/tjenester/datalager/GruppeDatatjeneste";
import {kunRequestRespons, mapTilData} from "felles-ng2/tjenester/datalager/DataOgMeta";
import {asynkArbeidPågårSignal} from "felles-ng2/tjenester/AsynkArbeidPågårSignal";
import {NgClass, NgTemplateOutlet} from "@angular/common";
import {KlikkMedStopPropagationDirektiv} from "web-ng2/delt/direktiver/klikkMedStopPropagation/klikkMedStopPropagation";
import {MatProgressSpinner} from "@angular/material/progress-spinner";
import {lesPlanPeriode, planperiodeSomTekst} from "felles-ng2/modell/planModellHjelpere";
import {GjeldendeTidssone} from "felles-ng2/tjenester/i18n/GjeldendeTidssone";
import {GjeldendeLokale} from "felles-ng2/tjenester/i18n/GjeldendeLokale";
import {i18n, Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";
import {MatIconModule} from "@angular/material/icon";
import {MatIconButton} from "@angular/material/button";
import {MatTooltip} from "@angular/material/tooltip";
import {Klokke} from "felles-ng2/tjenester/Klokke";
import {erIkkeTomListe, erListe} from "felles-ng2/støttefunksjoner/lister";
import {erMøteAvholdt} from "felles-ng2/modell/møteModellHjelpere";
import {erIkkeNil, erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {MatOption} from "@angular/material/core";
import {takeUntilDestroyed, toObservable, toSignal} from "@angular/core/rxjs-interop";
import {delay, map} from "rxjs/operators";

const debug = initDebugger('VelgMedlemKomponent');

/**  InjectionToken for injisering av {@link MøteTilgjengelighetForVelgMøterKomponent} implementasjon. */
export const MøteTilgjengelighetForVelgMøterKomponent_DI_TOKEN = new InjectionToken<MøteTilgjengelighetForVelgMøterKomponent>('MøteTilgjengelighetForVelgMøterKomponent implementasjon');

/**
 * Lar bruker velge møter blandt tilgjengelige grupper fra dropdown.
 * Merk at vi ikke støtter forhåndsvalgte møter via {@link VelgMøterKomponent#skjemakontroll},
 * dvs vi ekspanderer ikke stien ned til angitte møter i {@link VelgMøterKomponent#grupper} strutkuren.
 */
@Component({
    selector: 'velg-moeter',
    templateUrl: 'velg-møter.html',
    styleUrl: 'velg-møter.scss',
    providers: [{
        provide: MatFormFieldControl,
        useFactory: (komponent: VelgMøterKomponent) => new MatFormFieldControlProxy(() => komponent.nedtrekksmeny(), komponent.stateChanges),
        deps: [VelgMøterKomponent]
    }],
    imports: [
        MatSelectModule,
        ReactiveFormsModule,
        TranslateModule,
        FormaterZonedDateTimePipe,
        KlikkMedStopPropagationDirektiv,
        MatProgressSpinner,
        NgTemplateOutlet,
        MatIconModule,
        MatIconButton,
        NgClass,
        MatTooltip
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class VelgMøterKomponent {

    private nedtrekksmeny = viewChild.required<MatSelect>('moetevelger');

    skjemakontroll = input.required<FormControl<MøteModellEnkel[] | MøteModellEnkel | null>>();
    /** hvorvidt vi tillater valg av multiple møter, sann som standard */
    multiple = input(true, {transform: booleanAttribute});
    placeholder = input(this.oversettelser.oversett('Velg møter'));
    /** dersom ikke-null ekspanderes inneværende plan i angitt gruppe */
    ekspandertGruppe = input<GruppeId | null>(null);
    /** dersom true så må ekspandertGruppe være satt og kun møter fra denne gruppen velges */
    velgKunFraEkspandertGruppe = input(false, {transform: booleanAttribute});

    erValgt = harSammeId;
    /** Tilgjengelige grupper som møter kan velges fra */
    grupper = signal<Gruppe[]>([]);
    /** Liste med valgte møter */
    valgteMoeter = this.valgteMoeterSomSignal();
    /** Antallet unike grupper i settet av valgte møter */
    antallValgteGrupper = computed(() => this.valgteMoeter().reduce((møter, møte) => møter.add(møte.eiendeGruppeId), new Set<GruppeId>).size);

    private readonly stateChanges = new Subject<void>();

    constructor(private avhengigheter: VelgMøteKomponentAvhengigheter,
                @Inject(MøteTilgjengelighetForVelgMøterKomponent_DI_TOKEN) private møteTilgjengelighet: MøteTilgjengelighetForVelgMøterKomponent,
                private gjeldendeTidssone: GjeldendeTidssone,
                private gjeldendeLokale: GjeldendeLokale,
                private oversettelser: Oversettelser,
                private klokke: Klokke,
                private destroyRef: DestroyRef) {
        const dataLastet = this.initGrupper();
        this.scrollTilEkspandertGruppe(dataLastet);
    }

    gruppe(gruppeId: GruppeId): Gruppe {
        return this.grupper().find(gruppe => gruppe.gruppe.id === gruppeId)!;
    }

    moeteTooltip(møte: Møte) {
        return møte.tilgjengelig ?
          this.oversettelser.oversett(i18n('Møte i {gruppe} i {organisasjon}'), {gruppe: møte.gruppe.tittel, organisasjon: møte.gruppe.organisasjonsnavn}) :
          this.møteTilgjengelighet.årsak(møte.moete);
    }

    //hjelpemetoder

    private valgteMoeterSomSignal(): Signal<MøteModellEnkel[]> {
        return toSignal(toObservable(this.skjemakontroll).pipe(
          switchMap(skjemakontroll => skjemakontroll.valueChanges),
          map(møter => erNil(møter) ? [] : (erListe(møter) ? møter as MøteModellEnkel[] : [møter as MøteModellEnkel]))
        ), {initialValue: []})
    }

    private async initGrupper() {
        const avhengigheter: GrupperingMedBarnAvhengigheter = {
            klokke: this.klokke,
            gjeldendeTidssone: this.gjeldendeTidssone,
            gjeldendeLokale: this.gjeldendeLokale,
            oversettelser: this.oversettelser,
            planer: gruppeId => this.avhengigheter.planer(gruppeId),
            møter: planId => this.avhengigheter.møter(planId),
            erTilgjengeligForValg: async møte => await this.møteTilgjengelighet.erMøteTilgjengeligForValg(møte)
        };

        this.grupper.set(
          (await this.avhengigheter.grupper())
            .filter(gruppe => this.velgKunFraEkspandertGruppe() ? gruppe.id === this.ekspandertGruppe() : true)
            .map(gruppe => new Gruppe(avhengigheter, gruppe)))

        if (erIkkeNil(this.ekspandertGruppe()))
            await this.ekspanderInneværendePlanIGruppe(this.ekspandertGruppe()!);
    }

    private async ekspanderInneværendePlanIGruppe(gruppeId: GruppeId) {
        const gruppe = this.grupper().find(gruppe => gruppe.gruppe.id === gruppeId) ?? null;
        if (erNil(gruppe)) return;
        await gruppe.ekspander(this.nedtrekksmeny());
        const førsteIkkeHistoriskePlan = gruppe.barn().find(plan => !plan.erHistorisk) ?? null;
        if (erNil(førsteIkkeHistoriskePlan)) return;
        await førsteIkkeHistoriskePlan.ekspander(this.nedtrekksmeny());
    }

    /** Scroll til første tilgjengelige møte i nedtrekksmenyen når denne åpnes gitt
     * at ekspandertGruppe er angitt */
    private scrollTilEkspandertGruppe(dataLastet: Promise<void>) {
        toObservable(this.nedtrekksmeny).pipe(
          switchMap(select => select.openedChange), //lytt til at meny åpnes/lukkes
          filter(åpnet => åpnet), //vi bryr oss kun om at meny åpnes
          combineLatestWith(dataLastet), //vent til initGrupper() er ferdig
          //nå vet vi at menyen har blitt åpnet, og at initGrupper er ferdig (dvs forespurt gruppe og inneværende plan er lastet)
          //finn nå første møteinnslag som er koblet til gruppe angitt i ekspandertGruppe
          map(() => this.nedtrekksmeny().options.find((item: MatOption<MøteModellEnkel | null>) => item.value?.eiendeGruppeId === this.ekspandertGruppe()) as MatOption<MøteModellEnkel> | undefined),
          filter(erIkkeNil), //dersom vi ikke fant noen passende møteinnslag avbryter vi
          delay(200), //vent slik at nedtrekksmenyen rekker å tegne seg ut
          takeUntilDestroyed(this.destroyRef)
        )
          //scroll til første tilgjengelige møte i nedtrekksmenyen
          .subscribe(førsteTilgjengeligeMøte =>
            førsteTilgjengeligeMøte?._getHostElement().scrollIntoView({behavior: 'smooth'}))
    }
}


/** Tjeneste for å avgjøre om møte skal være tilgjengelig for valg i møter-valg-nedtrekksmenyen i {@link VelgMøterKomponent}. */
export interface MøteTilgjengelighetForVelgMøterKomponent {
    /** False dersom møtet ikke skal kunne velges i nedtrekksmeny */
    erMøteTilgjengeligForValg(møte: MøteModellEnkel): Promise<boolean>;

    /** Årsaken til at møtet ikke kan velges, vises som tooltip for møtet */
    årsak(møte: MøteModellEnkel): string;
}

/** Standard-implementasjon av {@link MøteTilgjengelighetForVelgMøterKomponent} som alltid returnerer sann.
 * Denne benyttes med mindre forelder-komponent har angitt annen implementasjon vhja providers */
export class StandardMøteTilgjengelighetForVelgMøterKomponent implements MøteTilgjengelighetForVelgMøterKomponent {
    async erMøteTilgjengeligForValg(møte: MøteModellEnkel): Promise<boolean> {
        return true;
    }

    årsak(møte: MøteModellEnkel): string {
        return "";
    }
}

@Injectable()
export class VelgMøteKomponentAvhengigheter {

    constructor(private gruppeDatatjeneste: GruppeDatatjeneste, private planRessurs: PlanRessursClient) {
    }

    grupper(): Promise<GruppeModell[]> {
        return firstValueFrom(this.gruppeDatatjeneste.tilgjengeligeGrupperMedMeta().pipe(kunRequestRespons(), mapTilData()));
    }

    planer(gruppeId: GruppeId): Promise<EnkelPlanModell[]> {
        return this.planRessurs.hentAlleForGrupper({gruppeIder: [gruppeId]}).somPromise();
    }

    async møter(planId: PlanId): Promise<MøteModellEnkel[]> {
        const plan = await this.planRessurs.hent(planId).somPromise();
        return plan.moeter;
    }
}

abstract class GrupperingMedBarn<T> {
    barn = signal<T[]>([]);
    laster = asynkArbeidPågårSignal();
    lastet = signal(false);
    ekspandert = signal(false);

    async ekspanderEllerKolaps($event: MouseEvent, møtevelgerMeny: MatSelect) {
        debug(`ekspanderEllerKolaps ${this.constructor.name}`, $event)
        if (this.ekspandert()) this.kolaps();
        else await this.ekspander(møtevelgerMeny);
    }

    async ekspander(møtevelgerMeny: MatSelect) {
        debug(`ekspander ${this.constructor.name}`)
        if (this.ekspandert()) return;
        this.ekspandert.set(true);
        await this.laster.utfør(async () => {
            this.barn.set(await this.lastBarn());
            this.lastet.set(true);
        });
        //scroll litt ned slik at nye elementer blir synlige - men kun dersom panelet som viser gruppene er synlig
        if (møtevelgerMeny.panelOpen)
            this.gjørNyeElementerSynlig(møtevelgerMeny);
    }

    /** Sørger for å scrolle litt ned for å gi bruker et hint om at nytt innhold har blitt lag til.
     Utføres ETTER at nye elementer har blitt lagt til og tegnet ut i GUI */
    gjørNyeElementerSynlig(møtevelgerMeny: MatSelect) {
        //sørger for å scrolle litt ned for å gi bruker et hint om at nytt innhold har blitt lag til
        //ETTER at nye elementer har blitt lagt til og tegnet ut i GUI
        requestAnimationFrame(() => {
            const skrollpanel = møtevelgerMeny.panel.nativeElement;
            skrollpanel.scrollTop = skrollpanel.scrollTop + 40;
        });
    }

    kolaps() {
        debug(`kolaps ${this.constructor.name}`)
        this.ekspandert.set(false);
    }

    abstract lastBarn(): Promise<T[]>;
}

interface GrupperingMedBarnAvhengigheter {
    klokke: Klokke;
    gjeldendeTidssone: GjeldendeTidssone;
    gjeldendeLokale: GjeldendeLokale;
    oversettelser: Oversettelser;

    planer(gruppeId: GruppeId): Promise<EnkelPlanModell[]>;

    møter(planId: PlanId): Promise<MøteModellEnkel[]>;

    erTilgjengeligForValg(møte: MøteModellEnkel): Promise<boolean>;
}

class Gruppe extends GrupperingMedBarn<Plan> {

    readonly gruppetittel: string;
    historiskePlaner = computed(() => this.barn().filter(plan => plan.erHistorisk));
    harHistoriskePlaner = computed(() => erIkkeTomListe(this.historiskePlaner()));
    viserHistoriskePlaner = signal(false);

    constructor(private avhengigheter: GrupperingMedBarnAvhengigheter, public gruppe: EnkelGruppeModell) {
        super();
        this.gruppetittel = avhengigheter.oversettelser.oversett('{gruppe} i {organisasjon}', {gruppe: gruppe.tittel, organisasjon: gruppe.organisasjonsnavn});
    }

    async lastBarn(): Promise<Plan[]> {
        const planer = await this.avhengigheter.planer(this.gruppe.id);
        return planer.map(plan => new Plan(this.avhengigheter, plan, this.gruppe));
    }

    visHistoriskePlaner(møtevelgerMeny: MatSelect) {
        this.viserHistoriskePlaner.set(true);
        this.gjørNyeElementerSynlig(møtevelgerMeny);
    }
}

class Plan extends GrupperingMedBarn<Møte> {

    readonly formatertPeriode: string;
    readonly erHistorisk: boolean;

    avholdteMoeter = computed(() => this.barn().filter(møte => møte.erAvholdt));
    harAvholdteMoeter = computed(() => erIkkeTomListe(this.avholdteMoeter()));
    viserAvholdteMoeter = signal(false);

    constructor(private avhengigheter: GrupperingMedBarnAvhengigheter, public plan: EnkelPlanModell, private gruppe: EnkelGruppeModell) {
        super();
        const planPeriode = lesPlanPeriode(this.plan);
        this.formatertPeriode = planperiodeSomTekst(planPeriode, this.avhengigheter.gjeldendeTidssone.brukersTidssone(), this.avhengigheter.gjeldendeLokale.gruppensLokale(this.plan.gruppeId));
        this.erHistorisk = planPeriode.erPassert(this.avhengigheter.klokke);
    }

    async lastBarn(): Promise<Møte[]> {
        const møter = await this.avhengigheter.møter(this.plan.id);
        const møterOgtilgjengelig = [];
        for (const møte of møter) møterOgtilgjengelig.push({
              moete: møte,
              gruppe: this.gruppe,
              tilgjengelig: await this.avhengigheter.erTilgjengeligForValg(møte),
              erAvholdt: erMøteAvholdt(møte, this.avhengigheter.klokke)
          }
        );
        return møterOgtilgjengelig;
    }

    visAvholdteMoeter(møtevelgerMeny: MatSelect) {
        this.viserAvholdteMoeter.set(true);
        this.gjørNyeElementerSynlig(møtevelgerMeny);
    }
}

interface Møte {
    moete: MøteModellEnkel;
    gruppe: EnkelGruppeModell;
    tilgjengelig: boolean;
    erAvholdt: boolean;
}
